import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { useAuth } from '../contexts/AuthContext';
import {
  Globe, Plus, Edit2, Trash2, FileText, Loader2, X, ChevronRight,
  ArrowLeft, Download, Sparkles
} from 'lucide-react';
import BrandVoiceInterview from './BrandVoiceInterview';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import { marked } from 'marked';
import DOMPurify from 'dompurify';

// Render overrides so GFM tables/headings look right in-app without depending on a
// Tailwind typography plugin. The AI brand report is Markdown (GFM pipe tables).
const mdComponents = {
  h1: (p) => <h1 className="font-serif text-2xl mt-6 mb-2" {...p} />,
  h2: (p) => <h2 className="font-serif text-xl mt-5 mb-2" {...p} />,
  h3: (p) => <h3 className="font-display text-sm font-medium mt-4 mb-1.5" {...p} />,
  p: (p) => <p className="mb-3 leading-relaxed" {...p} />,
  ul: (p) => <ul className="list-disc pl-5 mb-3 space-y-1" {...p} />,
  ol: (p) => <ol className="list-decimal pl-5 mb-3 space-y-1" {...p} />,
  table: (p) => <div className="overflow-x-auto my-4"><table className="w-full border-collapse text-xs" {...p} /></div>,
  th: (p) => <th className="border border-[var(--color-border)] px-2 py-1 text-left bg-[var(--color-surface)] font-medium" {...p} />,
  td: (p) => <td className="border border-[var(--color-border)] px-2 py-1 align-top" {...p} />,
  a: (p) => <a className="text-[var(--color-coral)] underline" {...p} />,
};

// Minimal standalone stylesheet for the downloaded HTML file.
const REPORT_DOWNLOAD_STYLE = `
  body{font-family:-apple-system,Segoe UI,Roboto,Helvetica,Arial,sans-serif;color:#2b2b2b;max-width:800px;margin:2rem auto;padding:0 1.25rem;line-height:1.6}
  h1,h2,h3{font-weight:600;line-height:1.25;margin:1.6em 0 .5em}
  h1{font-size:1.8rem}h2{font-size:1.35rem}h3{font-size:1.1rem}
  p{margin:.6em 0}ul,ol{margin:.6em 0 .6em 1.4em}
  table{border-collapse:collapse;width:100%;margin:1em 0;font-size:.9rem}
  th,td{border:1px solid #ddd;padding:.5em .6em;text-align:left;vertical-align:top}
  th{background:#f5f2ee}
  code{background:#f5f2ee;padding:.1em .3em;border-radius:3px}
`;

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const BrandVoice = ({ onNavigate }) => {
  const { getAuthHeaders, subscription, refreshSubscription } = useAuth();
  const [voices, setVoices] = useState([]);
  const [reports, setReports] = useState([]);
  const [loading, setLoading] = useState(true);
  const [view, setView] = useState('list'); // list | form | report
  const [editingVoice, setEditingVoice] = useState(null);
  const [activeReport, setActiveReport] = useState(null);
  const [generating, setGenerating] = useState(null);

  const [form, setForm] = useState({
    name: '', industry: '', target_audience: '', tone: 'professional',
    style: 'clear and concise', brand_values: '', unique_selling_points: '',
    competitors: '', website_url: '', example_content: ''
  });

  useEffect(() => { fetchData(); }, []);

  const fetchData = async () => {
    try {
      const [voicesRes, reportsRes] = await Promise.all([
        axios.get(`${API}/brand/voices`, { headers: getAuthHeaders() }),
        axios.get(`${API}/brand/reports`, { headers: getAuthHeaders() })
      ]);
      setVoices(voicesRes.data);
      setReports(reportsRes.data);
    } catch (err) {
      console.error('Failed to fetch brand data:', err);
    } finally {
      setLoading(false);
    }
  };

  const handleSave = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      if (editingVoice) {
        await axios.put(`${API}/brand/voices/${editingVoice}`, form, { headers: getAuthHeaders() });
      } else {
        await axios.post(`${API}/brand/voices`, form, { headers: getAuthHeaders() });
      }
      await fetchData();
      setView('list');
      resetForm();
    } catch (err) {
      alert(err.response?.data?.detail || 'Failed to save brand voice');
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Delete this brand voice profile?')) return;
    try {
      await axios.delete(`${API}/brand/voices/${id}`, { headers: getAuthHeaders() });
      setVoices(voices.filter(v => v.id !== id));
    } catch (err) {
      console.error('Failed to delete:', err);
    }
  };

  const handleGenerateReport = async (voiceId) => {
    setGenerating(voiceId);
    try {
      const response = await axios.post(`${API}/brand/report?voice_id=${voiceId}`, {}, { headers: getAuthHeaders() });
      setReports([response.data, ...reports]);
      setActiveReport(response.data);
      setView('report');
      await refreshSubscription();
    } catch (err) {
      alert(err.response?.data?.detail || 'Failed to generate report');
    } finally {
      setGenerating(null);
    }
  };

  const startEdit = (voice) => {
    setEditingVoice(voice.id);
    setForm({
      name: voice.name || '', industry: voice.industry || '',
      target_audience: voice.target_audience || '', tone: voice.tone || 'professional',
      style: voice.style || '', brand_values: voice.brand_values || '',
      unique_selling_points: voice.unique_selling_points || '',
      competitors: voice.competitors || '', website_url: voice.website_url || '',
      example_content: voice.example_content || ''
    });
    setView('form');
  };

  const resetForm = () => {
    setEditingVoice(null);
    setForm({
      name: '', industry: '', target_audience: '', tone: 'professional',
      style: 'clear and concise', brand_values: '', unique_selling_points: '',
      competitors: '', website_url: '', example_content: ''
    });
  };

  // Canonical limit comes from the /subscription payload (TIER_CONFIG-backed).
  // Gate the wizard here so at-limit users never lose input; backend 403 backstops.
  const maxVoices = subscription?.max_brand_voices;
  const atVoiceLimit = typeof maxVoices === 'number' && voices.length >= maxVoices;

  const startNewVoice = () => {
    if (atVoiceLimit) { onNavigate?.('credits'); return; }
    resetForm();
    setView('interview');
  };

  if (loading && voices.length === 0) {
    return <div className="flex items-center justify-center h-64"><div className="spinner"></div></div>;
  }

  // ============ REPORT VIEW ============
  if (view === 'report' && activeReport) {
    return (
      <div className="fade-in" data-testid="brand-report-view">
        <button onClick={() => setView('list')} className="flex items-center gap-2 text-[var(--color-muted)] hover:text-[var(--color-charcoal)] mb-6 text-sm transition-colors">
          <ArrowLeft className="w-4 h-4" /> Back to Brand Voices
        </button>
        <div className="flex items-start justify-between mb-6">
          <div>
            <h1 className="font-serif text-3xl mb-1">Brand Report</h1>
            <p className="text-sm text-[var(--color-muted)]">
              {activeReport.voice_name} &middot; Generated {new Date(activeReport.created_at).toLocaleDateString()}
            </p>
          </div>
          <button onClick={() => {
            // Markdown -> HTML (marked) -> sanitize (DOMPurify) -> minimal styled standalone doc.
            const bodyHtml = DOMPurify.sanitize(marked.parse(activeReport.report || ''));
            const doc = `<!DOCTYPE html><html lang="en"><head><meta charset="utf-8" />` +
              `<meta name="viewport" content="width=device-width, initial-scale=1" />` +
              `<title>Brand Report — ${activeReport.voice_name}</title>` +
              `<style>${REPORT_DOWNLOAD_STYLE}</style></head><body>${bodyHtml}</body></html>`;
            const blob = new Blob([doc], { type: 'text/html' });
            const url = URL.createObjectURL(blob);
            const a = document.createElement('a');
            a.href = url; a.download = `brand-report-${activeReport.voice_name}.html`;
            a.click(); URL.revokeObjectURL(url);
          }} className="btn btn-secondary" data-testid="download-report-btn">
            <Download className="w-4 h-4" /> Download HTML
          </button>
        </div>

        {/* Use Cases Banner */}
        <div className="card p-5 mb-6 bg-[var(--color-surface)]" style={{boxShadow: 'none'}}>
          <p className="font-display text-xs font-medium mb-2">How to use this report:</p>
          <div className="grid md:grid-cols-4 gap-3 text-xs text-[var(--color-muted)]">
            <div className="flex items-start gap-2">
              <span className="text-[var(--color-coral)] font-bold flex-shrink-0">1.</span>
              Hand the <strong className="text-[var(--color-charcoal)]">Brand Kit</strong> to your designer (Canva, Freepik, freelancer) for logo & visual identity
            </div>
            <div className="flex items-start gap-2">
              <span className="text-[var(--color-coral)] font-bold flex-shrink-0">2.</span>
              Use the analysis to build a <strong className="text-[var(--color-charcoal)]">GTM strategy</strong> with your AI tool or consultant
            </div>
            <div className="flex items-start gap-2">
              <span className="text-[var(--color-coral)] font-bold flex-shrink-0">3.</span>
              Apply the <strong className="text-[var(--color-charcoal)]">color palette & typography</strong> to your website theme
            </div>
            <div className="flex items-start gap-2">
              <span className="text-[var(--color-coral)] font-bold flex-shrink-0">4.</span>
              Use content pillars as topics for your <strong className="text-[var(--color-charcoal)]">first 10 Stilos articles</strong>
            </div>
          </div>
        </div>

        <div className="card p-8">
          <div className="max-w-none text-sm" data-testid="report-content">
            <ReactMarkdown remarkPlugins={[remarkGfm]} components={mdComponents}>
              {activeReport.report || ''}
            </ReactMarkdown>
          </div>
        </div>
      </div>
    );
  }

  // ============ INTERVIEW VIEW (guided create — ROADMAP §4) ============
  if (view === 'interview') {
    return (
      <BrandVoiceInterview
        API={API}
        getAuthHeaders={getAuthHeaders}
        onComplete={async () => { await fetchData(); await refreshSubscription(); setView('list'); }}
        onCancel={() => setView('list')}
      />
    );
  }

  // ============ FORM VIEW (edit details — flat form) ============
  if (view === 'form') {
    return (
      <div className="fade-in max-w-2xl" data-testid="brand-voice-form">
        <button onClick={() => { setView('list'); resetForm(); }} className="flex items-center gap-2 text-[var(--color-muted)] hover:text-[var(--color-charcoal)] mb-6 text-sm transition-colors">
          <ArrowLeft className="w-4 h-4" /> Back to Brand Voices
        </button>
        <h1 className="font-serif text-3xl mb-2">Edit details</h1>
        <p className="text-sm text-[var(--color-muted)] mb-8">
          Refine your brand's identity fields. To add citable facts (results, pricing, testimonials),
          use the guided interview from the Brand Voice list.
        </p>

        <form onSubmit={handleSave} className="space-y-5">
          <div className="card p-6 space-y-4">
            <h3 className="font-display text-sm font-medium">Brand Identity</h3>
            <div>
              <label className="block text-xs font-medium mb-1.5">Brand Name *</label>
              <input type="text" value={form.name} onChange={(e) => setForm({...form, name: e.target.value})}
                className="input" placeholder="e.g., Stilos" required data-testid="voice-name-input" />
            </div>
            <div>
              <label className="block text-xs font-medium mb-1.5">Industry *</label>
              <input type="text" value={form.industry} onChange={(e) => setForm({...form, industry: e.target.value})}
                className="input" placeholder="e.g., AI Content Technology, E-commerce, Health & Wellness" required />
            </div>
            <div>
              <label className="block text-xs font-medium mb-1.5">Website URL</label>
              <input type="url" value={form.website_url} onChange={(e) => setForm({...form, website_url: e.target.value})}
                className="input" placeholder="https://example.com" />
            </div>
          </div>

          <div className="card p-6 space-y-4">
            <h3 className="font-display text-sm font-medium">Voice & Style</h3>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="block text-xs font-medium mb-1.5">Tone</label>
                <select value={form.tone} onChange={(e) => setForm({...form, tone: e.target.value})} className="input">
                  <option value="professional">Professional</option>
                  <option value="casual">Casual</option>
                  <option value="friendly">Friendly</option>
                  <option value="authoritative">Authoritative</option>
                  <option value="conversational">Conversational</option>
                  <option value="inspiring">Inspiring</option>
                </select>
              </div>
              <div>
                <label className="block text-xs font-medium mb-1.5">Writing Style</label>
                <input type="text" value={form.style} onChange={(e) => setForm({...form, style: e.target.value})}
                  className="input" placeholder="e.g., clear and concise, storytelling-driven" />
              </div>
            </div>
            <div>
              <label className="block text-xs font-medium mb-1.5">Brand Values</label>
              <input type="text" value={form.brand_values} onChange={(e) => setForm({...form, brand_values: e.target.value})}
                className="input" placeholder="e.g., Innovation, Authenticity, Simplicity" />
            </div>
          </div>

          <div className="card p-6 space-y-4">
            <h3 className="font-display text-sm font-medium">Market & Audience</h3>
            <div>
              <label className="block text-xs font-medium mb-1.5">Target Audience *</label>
              <textarea value={form.target_audience} onChange={(e) => setForm({...form, target_audience: e.target.value})}
                className="input min-h-[80px] resize-y" placeholder="Describe your ideal customer..." required />
            </div>
            <div>
              <label className="block text-xs font-medium mb-1.5">Unique Selling Points</label>
              <textarea value={form.unique_selling_points} onChange={(e) => setForm({...form, unique_selling_points: e.target.value})}
                className="input min-h-[80px] resize-y" placeholder="What makes you different from competitors?" />
            </div>
            <div>
              <label className="block text-xs font-medium mb-1.5">Competitors</label>
              <input type="text" value={form.competitors} onChange={(e) => setForm({...form, competitors: e.target.value})}
                className="input" placeholder="e.g., Jasper AI, Surfer SEO, HubSpot" />
            </div>
          </div>

          <div className="card p-6 space-y-4">
            <h3 className="font-display text-sm font-medium">Example Content (optional)</h3>
            <div>
              <label className="block text-xs font-medium mb-1.5">Paste example text that represents your brand voice</label>
              <textarea value={form.example_content} onChange={(e) => setForm({...form, example_content: e.target.value})}
                className="input min-h-[100px] resize-y" placeholder="Paste a paragraph from your website, blog, or marketing materials..." />
            </div>
          </div>

          <div className="flex gap-3">
            <button type="button" onClick={() => { setView('list'); resetForm(); }} className="btn btn-secondary flex-1">Cancel</button>
            <button type="submit" className="btn btn-primary flex-1" data-testid="save-voice-btn">
              {editingVoice ? 'Save Changes' : 'Create Brand Voice'}
            </button>
          </div>
        </form>
      </div>
    );
  }

  // ============ LIST VIEW ============
  return (
    <div className="fade-in" data-testid="brand-voice-page">
      <div className="flex flex-col md:flex-row justify-between items-start md:items-center gap-4 mb-8">
        <div>
          <h1 className="font-serif text-3xl mb-1">Brand Voice</h1>
          <p className="text-sm text-[var(--color-muted)]">
            Define your brand identity. Generate actionable brand reports.
          </p>
        </div>
        <button onClick={startNewVoice} className="btn btn-primary" data-testid="new-voice-btn">
          {atVoiceLimit
            ? <><Sparkles className="w-4 h-4" /> Upgrade for more voices</>
            : <><Plus className="w-4 h-4" /> New Brand Voice</>}
        </button>
      </div>

      {voices.length === 0 ? (
        <div className="card p-14 text-center">
          <div className="w-14 h-14 mx-auto rounded-full flex items-center justify-center mb-4 bg-[var(--color-surface)]">
            <Globe className="w-7 h-7 text-[var(--color-coral)]" />
          </div>
          <h3 className="font-display text-base font-medium mb-2">No brand voices yet</h3>
          <p className="text-sm text-[var(--color-muted)] mb-3">
            Create a brand voice profile to generate content that sounds like you and get an actionable brand analysis.
          </p>
          <p className="text-xs text-[var(--color-muted)] mb-6">
            Use your brand report to brief designers on Canva/Freepik, plan your GTM strategy, and create targeted content pillars.
          </p>
          <button onClick={startNewVoice} className="btn btn-primary text-sm">
            <Plus className="w-4 h-4" /> Create your first brand voice
          </button>
        </div>
      ) : (
        <div className="space-y-4">
          {voices.map((voice) => {
            const voiceReports = reports.filter(r => r.voice_id === voice.id);
            return (
              <div key={voice.id} className="card p-6" data-testid={`voice-${voice.id}`}>
                <div className="flex items-start justify-between">
                  <div className="flex-1">
                    <div className="flex items-center gap-3 mb-2">
                      <div className="w-10 h-10 rounded-xl flex items-center justify-center bg-[var(--color-surface)]">
                        <Globe className="w-5 h-5 text-[var(--color-coral)]" />
                      </div>
                      <div>
                        <h3 className="font-display text-base font-medium">{voice.name}</h3>
                        <p className="text-[10px] text-[var(--color-muted)]">{voice.industry} &middot; {voice.tone}</p>
                      </div>
                    </div>
                    {voice.target_audience && (
                      <p className="text-xs text-[var(--color-muted)] mb-3 line-clamp-2 ml-[52px]">{voice.target_audience}</p>
                    )}
                    <div className="flex flex-wrap gap-2 ml-[52px]">
                      {voice.brand_values && voice.brand_values.split(',').map((v, i) => (
                        <span key={i} className="text-[10px] px-2 py-0.5 rounded-full bg-[var(--color-surface)] text-[var(--color-muted)]">
                          {v.trim()}
                        </span>
                      ))}
                    </div>
                  </div>
                  <div className="flex items-center gap-2">
                    <button onClick={() => handleGenerateReport(voice.id)} disabled={generating === voice.id}
                      className="btn btn-accent text-xs" data-testid={`gen-report-${voice.id}`}>
                      {generating === voice.id ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <><Sparkles className="w-3.5 h-3.5" /> Report</>}
                    </button>
                    <button onClick={() => startEdit(voice)} className="btn btn-ghost p-2"><Edit2 className="w-3.5 h-3.5" /></button>
                    <button onClick={() => handleDelete(voice.id)} className="btn btn-ghost p-2"><Trash2 className="w-3.5 h-3.5" /></button>
                  </div>
                </div>
                
                {voiceReports.length > 0 && (
                  <div className="mt-4 pt-4 border-t border-[var(--color-border)] ml-[52px]">
                    <p className="text-[10px] font-medium text-[var(--color-muted)] mb-2">RECENT REPORTS</p>
                    <div className="space-y-1.5">
                      {voiceReports.slice(0, 3).map((report) => (
                        <button key={report.id} onClick={() => { setActiveReport(report); setView('report'); }}
                          className="flex items-center gap-2 text-xs w-full text-left p-2 rounded-lg hover:bg-[var(--color-surface)] transition-colors">
                          <FileText className="w-3.5 h-3.5 text-[var(--color-coral)]" />
                          <span>Brand Report — {new Date(report.created_at).toLocaleDateString()}</span>
                          <ChevronRight className="w-3 h-3 text-[var(--color-muted)] ml-auto" />
                        </button>
                      ))}
                    </div>
                  </div>
                )}
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
};

export default BrandVoice;
